import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EventType, } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useAppConfig } from '../appConfig';
import { useStateAfterLoginComplete } from './redirectState/useStateAfterLoginComplete';
import { useLoginRequest } from './useLoginRequest';
import { MsalAuthErrorCode } from './types';
import { useAcquireToken } from './useAcquireToken';
/** Effect hook that configures callback handlers for our MSAL instance */
export const useMsrcMsalCallbackHandlerEffect = (setError, setErrorSilent) => {
    const { instance, accounts } = useMsal();
    const { push } = useHistory();
    const loginRequest = useLoginRequest();
    const { usePopupFlow } = useAppConfig();
    const handleStateAfterLogin = useStateAfterLoginComplete();
    const acquireTokenSilent = useAcquireToken();
    const [isApiTokenLoaded, setApiTokenIsLoaded] = useState(false);
    useEffect(() => {
        // This will be run on component mount
        const callbackId = instance.addEventCallback(callbackMessage => {
            // This will be run every time an event is emitted after registering this callback
            switch (callbackMessage.eventType) {
                case EventType.LOGOUT_SUCCESS: {
                    const payload = callbackMessage.payload;
                    if (payload.state) {
                        if (!usePopupFlow) {
                            handleStateAfterLogin(payload.state);
                        }
                    }
                    break;
                }
                case EventType.LOGIN_SUCCESS: {
                    const payload = callbackMessage.payload;
                    if (payload.state) {
                        setTimeout(() => {
                            if (!usePopupFlow) {
                                handleStateAfterLogin(payload.state);
                            }
                            setError(undefined);
                            setErrorSilent(undefined);
                        }, 300);
                    }
                    if (payload.account) {
                        instance.setActiveAccount(payload.account);
                    }
                    acquireTokenSilent().then(response => {
                        setApiTokenIsLoaded(!!response.accessToken);
                    });
                    break;
                }
                case EventType.ACQUIRE_TOKEN_SUCCESS: {
                    const payload = callbackMessage.payload;
                    setApiTokenIsLoaded(!!payload.accessToken);
                    break;
                }
                case EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE:
                case EventType.SSO_SILENT_FAILURE: {
                    const authError = callbackMessage.error;
                    if (((authError === null || authError === void 0 ? void 0 : authError.errorCode) === MsalAuthErrorCode.interaction_required ||
                        (authError === null || authError === void 0 ? void 0 : authError.errorCode)) === MsalAuthErrorCode.block_iframe_reload) {
                        // These are errors we don't care about.
                        break;
                    }
                    if (callbackMessage === null || callbackMessage === void 0 ? void 0 : callbackMessage.error) {
                        console.warn(callbackMessage.error);
                        setErrorSilent(callbackMessage.error);
                    }
                    else {
                        setErrorSilent(undefined);
                    }
                    break;
                }
                case EventType.LOGOUT_FAILURE:
                case EventType.LOGIN_FAILURE: {
                    if (callbackMessage === null || callbackMessage === void 0 ? void 0 : callbackMessage.error) {
                        console.error(callbackMessage.error);
                        setError(callbackMessage.error);
                    }
                    else {
                        setError(undefined);
                    }
                    break;
                }
            }
        });
        return () => {
            // This will be run on component unmount
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
        //do not need to worry about handleStateAfterLogin
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        instance,
        setApiTokenIsLoaded,
        setError,
        setErrorSilent,
        loginRequest,
        push,
        usePopupFlow,
    ]);
    useEffect(() => {
        var _a;
        if (((_a = accounts[0]) === null || _a === void 0 ? void 0 : _a.idTokenClaims) && !isApiTokenLoaded) {
            acquireTokenSilent();
        }
    }, [accounts, isApiTokenLoaded, acquireTokenSilent]);
    return { isApiTokenLoaded };
};
